import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  FunnelIcon,
  UsersIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  XMarkIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import productAnalyticsService from '../../services/api/product-analytics.service';

export default function FunnelDetailsPage() {
  const { t } = useTranslation('analytics');
  const { id } = useParams<{ id: string }>();
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '7d',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [funnel, setFunnel] = useState<any>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [prevAnalysis, setPrevAnalysis] = useState<any>(null);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [stepUsersModal, setStepUsersModal] = useState<{ stepOrder: number; eventName: string } | null>(null);
  const [stepUsers, setStepUsers] = useState<any[]>([]);
  const [stepUsersTotal, setStepUsersTotal] = useState(0);
  const [stepUsersLoading, setStepUsersLoading] = useState(false);
  const [stepUsersPage, setStepUsersPage] = useState(1);

  // Load funnel data + previous period comparison
  useEffect(() => {
    let cancelled = false;

    const loadFunnelData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const startTime = new Date(timeRange.startTime);
        const endTime = new Date(timeRange.endTime);
        const duration = endTime.getTime() - startTime.getTime();
        const prevStart = new Date(startTime.getTime() - duration);
        const prevEnd = new Date(startTime.getTime());

        const [funnelData, analysisData, prevData] = await Promise.all([
          productAnalyticsService.getFunnel(id),
          productAnalyticsService.analyzeFunnel(id, startTime.toISOString(), endTime.toISOString()),
          productAnalyticsService.analyzeFunnel(id, prevStart.toISOString(), prevEnd.toISOString()).catch(() => null),
        ]);

        if (!cancelled) {
          setFunnel(funnelData);
          setAnalysis(analysisData);
          setPrevAnalysis(prevData);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading funnel:', err);
          setError(err.message || 'Failed to load funnel data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFunnelData();
    return () => { cancelled = true; };
  }, [id, timeRange.startTime, timeRange.endTime]);

  // Load step users
  useEffect(() => {
    if (!stepUsersModal || !id) return;
    setStepUsersLoading(true);

    productAnalyticsService.getFunnelStepUsers(
      id,
      stepUsersModal.stepOrder,
      new Date(timeRange.startTime).toISOString(),
      new Date(timeRange.endTime).toISOString(),
      20,
      (stepUsersPage - 1) * 20
    )
      .then(data => {
        setStepUsers(data.users || []);
        setStepUsersTotal(data.total || 0);
      })
      .catch(() => { setStepUsers([]); setStepUsersTotal(0); })
      .finally(() => setStepUsersLoading(false));
  }, [stepUsersModal, id, stepUsersPage, timeRange]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${Math.floor(ms / 1000)}s`;
  };

  const getConversionColor = (rate: number): string => {
    if (rate >= 70) return 'text-green-600';
    if (rate >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  // Comparison helpers
  const getComparisonChange = (current: number, previous: number | undefined): { change: number; direction: 'up' | 'down' | 'same' } | null => {
    if (previous === undefined || previous === null || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return { change: Math.abs(Math.round(change * 10) / 10), direction: change > 0 ? 'up' : change < 0 ? 'down' : 'same' };
  };

  const ComparisonBadge = ({ current, previous, invertColor = false }: { current: number; previous?: number; invertColor?: boolean }) => {
    const comp = getComparisonChange(current, previous);
    if (!comp) return null;
    const isGood = invertColor ? comp.direction === 'down' : comp.direction === 'up';
    return (
      <span className={`inline-flex items-center space-x-1 text-xs font-medium ${isGood ? 'text-green-600' : comp.direction === 'same' ? 'text-gray-400' : 'text-red-600'}`}>
        {comp.direction === 'up' ? <ArrowTrendingUpIcon className="w-3 h-3" /> : comp.direction === 'down' ? <ArrowTrendingDownIcon className="w-3 h-3" /> : null}
        <span>{comp.change}%</span>
      </span>
    );
  };

  const overallStats = analysis ? {
    totalEntries: analysis.totalEntries || 0,
    completions: analysis.completions || 0,
    overallConversion: analysis.overallConversionRate || 0,
    avgTimeToConvert: analysis.avgTimeToConvert ? formatTime(analysis.avgTimeToConvert) : 'N/A',
  } : { totalEntries: 0, completions: 0, overallConversion: 0, avgTimeToConvert: 'N/A' };

  const mappedSteps = analysis?.steps.map((step: any) => ({
    order: step.stepOrder,
    eventName: step.eventName,
    users: step.userCount,
    conversionRate: step.conversionRate,
    dropOffRate: step.dropOffRate,
    avgTimeToNext: step.avgTimeToNextStep ? formatTime(step.avgTimeToNextStep) : undefined,
  })) || [];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('funnels.details.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !funnel || !analysis) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <Link to="/analytics/funnels" className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('funnels.details.notFound')}</h1>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400">{error || t('funnels.details.notFoundMessage')}</p>
            <Link to="/analytics/funnels" className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              {t('funnels.details.backToFunnels')}
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/analytics/funnels" className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div>
              <div className="flex items-center space-x-3">
                <FunnelIcon className="w-6 h-6 text-cyan-600" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{funnel.name}</h1>
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{funnel.description}</p>
            </div>
          </div>
        </div>

        {/* Time Range */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Overall Stats with Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('funnels.analysis.overallConversion')}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <p className={`text-3xl font-bold ${getConversionColor(overallStats.overallConversion)}`}>{overallStats.overallConversion}%</p>
                  <ComparisonBadge current={overallStats.overallConversion} previous={prevAnalysis?.overallConversionRate} />
                </div>
              </div>
              <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg"><FunnelIcon className="w-6 h-6 text-cyan-600" /></div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('funnels.analysis.totalEntries')}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(overallStats.totalEntries)}</p>
                  <ComparisonBadge current={overallStats.totalEntries} previous={prevAnalysis?.totalEntries} />
                </div>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg"><UsersIcon className="w-6 h-6 text-purple-600" /></div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('funnels.analysis.completions')}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(overallStats.completions)}</p>
                  <ComparisonBadge current={overallStats.completions} previous={prevAnalysis?.completions} />
                </div>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"><ChartBarIcon className="w-6 h-6 text-green-600" /></div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('funnels.analysis.avgTimeToConvert')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{overallStats.avgTimeToConvert}</p>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg"><ClockIcon className="w-6 h-6 text-amber-600" /></div>
            </div>
          </div>
        </div>

        {/* Visual Funnel Chart with Tooltips */}
        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-purple-600 bg-clip-text text-transparent mb-2">
              {t('funnels.analysis.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('funnels.analysis.subtitle')}</p>
          </div>

          <div className="flex items-center justify-center">
            <div className="relative w-full max-w-5xl">
              {mappedSteps.map((step: any, index: number) => {
                const isLastStep = index === mappedSteps.length - 1;
                const dropOffUsers = !isLastStep ? step.users - (mappedSteps[index + 1]?.users || 0) : 0;

                // Width scales with conversion rate (minimum 30% so content fits)
                const widthPercent = Math.max(30, step.conversionRate);
                // Next step width for trapezoid shape
                const nextWidthPercent = !isLastStep ? Math.max(30, mappedSteps[index + 1]?.conversionRate || 30) : widthPercent * 0.7;
                // Calculate trapezoid inset (difference between current and next width)
                const insetPercent = ((widthPercent - nextWidthPercent) / widthPercent / 2) * 100;

                const colors = [
                  { bg: 'bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500', border: 'border-cyan-300', shadow: 'shadow-cyan-500/50' },
                  { bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500', border: 'border-pink-300', shadow: 'shadow-pink-500/50' },
                  { bg: 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500', border: 'border-orange-300', shadow: 'shadow-orange-500/50' },
                  { bg: 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500', border: 'border-green-300', shadow: 'shadow-green-500/50' },
                  { bg: 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500', border: 'border-indigo-300', shadow: 'shadow-indigo-500/50' },
                ];
                const colorSet = colors[index % colors.length];

                return (
                  <div key={step.order} className="relative mb-2">
                    <div
                      className="group relative mx-auto transition-all duration-500 hover:scale-105"
                      style={{ width: `${widthPercent}%`, height: '100px' }}
                      onMouseEnter={() => setHoveredStep(index)}
                      onMouseLeave={() => setHoveredStep(null)}
                    >
                      <div
                        className={`relative ${colorSet.bg} ${colorSet.shadow} shadow-2xl transition-all duration-500 group-hover:shadow-3xl`}
                        style={{
                          height: '100px',
                          clipPath: `polygon(0% 0%, 100% 0%, ${100 - insetPercent}% 100%, ${insetPercent}% 100%)`,
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-black/10" />

                        <div className="absolute inset-0 flex items-center justify-center text-white z-10"
                          style={{ paddingLeft: `${insetPercent + 5}%`, paddingRight: `${insetPercent + 5}%` }}
                        >
                          <div className="flex items-center justify-center w-8 h-8 bg-white/25 backdrop-blur-md rounded-lg border border-white/30 mr-3 flex-shrink-0">
                            <span className="text-sm font-black">{step.order}</span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate drop-shadow-md">{step.eventName}</div>
                            <div className="text-2xl font-black tracking-tight drop-shadow-lg">{step.conversionRate.toFixed(1)}%</div>
                          </div>

                          <div className="flex items-center space-x-3 text-sm font-semibold flex-shrink-0">
                            <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                              <UsersIcon className="w-4 h-4" />
                              <span>{formatNumber(step.users)}</span>
                            </div>
                            {step.avgTimeToNext && (
                              <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                                <ClockIcon className="w-4 h-4" />
                                <span>{step.avgTimeToNext}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Hover Tooltip */}
                      {hoveredStep === index && (
                        <div className="absolute right-4 top-2 z-20 bg-gray-900 text-white rounded-lg shadow-xl p-4 text-sm min-w-[200px]">
                          <p className="font-bold mb-2">{step.eventName}</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-gray-400">{t('funnels.details.usersLabel')}:</span><span>{formatNumber(step.users)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">{t('funnels.details.conversion')}:</span><span>{step.conversionRate.toFixed(1)}%</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">{t('funnels.details.dropOff')}:</span><span className="text-red-400">{step.dropOffRate.toFixed(1)}%</span></div>
                            {step.avgTimeToNext && <div className="flex justify-between"><span className="text-gray-400">{t('funnels.details.timeToNext')}:</span><span>{step.avgTimeToNext}</span></div>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Drop-off indicator */}
                    {!isLastStep && step.dropOffRate > 0 && (
                      <div className="flex items-center justify-center my-6 relative">
                        <div className="absolute left-1/2 transform -translate-x-1/2 top-0 w-1 h-4 bg-gradient-to-b from-red-200 to-red-400 dark:from-red-700 dark:to-red-500"></div>
                        <div className="relative bg-gradient-to-r from-red-50 via-red-100 to-red-50 dark:from-red-950 dark:via-red-900 dark:to-red-950 rounded-2xl px-8 py-4 shadow-lg border-2 border-red-300 dark:border-red-700 transform transition-all duration-300 hover:scale-105">
                          <div className="relative flex items-center space-x-6">
                            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg">
                              <ArrowTrendingDownIcon className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex items-center space-x-4">
                              <div className="text-center">
                                <div className="text-3xl font-black text-red-600 dark:text-red-400">{step.dropOffRate.toFixed(1)}%</div>
                                <div className="text-xs font-semibold text-red-500 uppercase tracking-wide">{t('funnels.details.dropOff')}</div>
                              </div>
                              <div className="h-12 w-px bg-gradient-to-b from-transparent via-red-300 to-transparent dark:via-red-600"></div>
                              <div className="text-center">
                                <div className="text-3xl font-black text-red-700 dark:text-red-300">{formatNumber(dropOffUsers)}</div>
                                <div className="text-xs font-semibold text-red-500 uppercase tracking-wide">{t('funnels.details.usersLost')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Analysis Table with View Users button */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('funnels.analysis.stepAnalysis')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.details.step')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.details.event')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.analysis.users')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.analysis.stepConversion')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.analysis.dropOff')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.analysis.timeToNextStep')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('funnels.details.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {mappedSteps.map((step: any) => (
                  <tr key={step.order} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center w-8 h-8 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-full font-semibold text-sm">
                        {step.order}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900 dark:text-white">{step.eventName}</p>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900 dark:text-white">{formatNumber(step.users)}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`font-bold ${getConversionColor(step.conversionRate)}`}>{step.conversionRate}%</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {step.dropOffRate > 0 ? (
                        <span className="text-red-600 dark:text-red-400 font-medium">{step.dropOffRate}%</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">{step.avgTimeToNext || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => { setStepUsersModal({ stepOrder: step.order, eventName: step.eventName }); setStepUsersPage(1); }}
                        className="inline-flex items-center space-x-1 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
                      >
                        <EyeIcon className="w-4 h-4" />
                        <span>{t('funnels.details.viewUsers')}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Step Users Modal */}
        {stepUsersModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setStepUsersModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('funnels.details.usersAtStep', { step: stepUsersModal.stepOrder })}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stepUsersModal.eventName} - {t('funnels.details.totalUsersCount', { count: stepUsersTotal })}</p>
                </div>
                <button onClick={() => setStepUsersModal(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {stepUsersLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                  </div>
                ) : stepUsers.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">{t('funnels.details.noUsersAtStep')}</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('funnels.details.userId')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('funnels.details.timestamp')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {stepUsers.map((user, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{user.userId}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{user.timestamp ? new Date(user.timestamp).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {stepUsersTotal > 20 && (
                <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {t('funnels.details.page', { current: stepUsersPage, total: Math.ceil(stepUsersTotal / 20) })}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setStepUsersPage(p => Math.max(1, p - 1))}
                      disabled={stepUsersPage === 1}
                      className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                    >
                      {t('funnels.details.previous')}
                    </button>
                    <button
                      onClick={() => setStepUsersPage(p => p + 1)}
                      disabled={stepUsersPage >= Math.ceil(stepUsersTotal / 20)}
                      className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 text-cyan-600 border-cyan-300"
                    >
                      {t('funnels.details.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
